import { useCallback, useContext } from 'react'
import { useSelector } from 'react-redux'

import { IS_PRODUCTION } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/constants'
import { useFlags } from '@lyrahealth-inc/shared-app-logic/src/features/flags/useFlags'
import { useWellnessCheckIn as useWellnessCheckInShared } from '@lyrahealth-inc/shared-app-logic/src/features/wellnessCheckIn/hooks/useWellnessCheckIn'
import {
  WellnessCheckInResponse,
  WellnessCheckInResults,
} from '@lyrahealth-inc/shared-app-logic/src/features/wellnessCheckIn/types'
import { ProductTypeContext } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/productType/productTypeContext'

import { getDeviceUUID } from '../../data/appGlobals/appGlobalsSelectors'
import { getLibraryDisabled } from '../../data/customer/customerSelectors'
import { useAppDispatch } from '../../data/storeConfiguration/store'
import {
  getId,
  getIsLoggedOutUserAssumedInternationalAndOfInternationalCustomer,
  getIsUserLoggedIn,
} from '../../data/user/userSelectors'
import {
  clearWellnessCheckInFormValues,
  clearWellnessCheckInResults,
  getWellnessCheckInResultsData,
  postWellnessCheckInResultsData,
  setWellnessCheckInResults,
} from '../../features/wellnessCheckIn/data/wellnessCheckInActions'
import {
  getWellnessCheckInResultsList,
  getWellnessCheckInStateResults,
} from '../../features/wellnessCheckIn/data/wellnessCheckInSelectors'
import { LOG_CATEGORY, logToSentry } from '../utils/userUtils'

export const useWellnessCheckIn = (): ReturnType<typeof useWellnessCheckInShared> & {
  wellnessCheckInResultsList: WellnessCheckInResponse[]
  storedWellnessCheckInStateResults: WellnessCheckInResults
} => {
  const dispatch = useAppDispatch()
  const { isLyraSelect } = useContext(ProductTypeContext)
  const {
    isWellnessCheckInEnabled,
    isWellnessCheckInBenefitsEntryPointEnabled,
    isHomebaseWellnessCheckInEntrypointEnabled,
  } = useFlags()

  const lyraId = useSelector(getId)
  const libraryDisabled = useSelector(getLibraryDisabled)
  const deviceUUID = useSelector(getDeviceUUID)
  const storedWellnessCheckInStateResults = useSelector(getWellnessCheckInStateResults)
  const wellnessCheckInResultsList = useSelector(getWellnessCheckInResultsList)
  const isLoggedIn = useSelector(getIsUserLoggedIn)
  const isUserAssumedInternational = useSelector(getIsLoggedOutUserAssumedInternationalAndOfInternationalCustomer)
  const hideWellnessCheckInForLoggedOutUser = !isLoggedIn && (isUserAssumedInternational || isLyraSelect)

  const logger = useCallback(
    (data: { message: string; type: string }) => {
      logToSentry(IS_PRODUCTION ? LOG_CATEGORY.PROD_DEBUG : LOG_CATEGORY.STAGING_DEBUG, { userId: lyraId, ...data })
    },
    [lyraId],
  )

  return {
    wellnessCheckInResultsList,
    storedWellnessCheckInStateResults,
    ...useWellnessCheckInShared({
      isWellnessCheckInEnabled,
      isWellnessCheckInBenefitsEntryPointEnabled,
      isHomebaseWellnessCheckInEntrypointEnabled,
      libraryDisabled,
      hideWellnessCheckInForLoggedOutUser,
      storedWellnessCheckInStateResults,
      getWellnessCheckInResultsData: useCallback(
        async () => await dispatch(getWellnessCheckInResultsData({ lyraId })).unwrap(),
        [dispatch, lyraId],
      ),
      setWellnessCheckInResults: useCallback((values) => dispatch(setWellnessCheckInResults(values)), [dispatch]),
      clearWellnessCheckInFormValues: useCallback(() => dispatch(clearWellnessCheckInFormValues()), [dispatch]),
      clearWellnessCheckInResults: useCallback(() => dispatch(clearWellnessCheckInResults()), [dispatch]),
      postWellnessCheckInResults: useCallback(
        async (values) =>
          await dispatch(
            postWellnessCheckInResultsData({ lyraId, requestBody: { deviceUUID, body: JSON.stringify(values) } }),
          ).unwrap(),
        [dispatch, lyraId, deviceUUID],
      ),
      logger,
    }),
  }
}
